import { Gathering } from '@cobu/types';
import { authenticatedApiClient, unauthenticatedApiClient } from './axios';
import { UserInfo, Restriction, MyRestrictionResponse } from '@cobu/types';
import {
  USER_PUBLIC_PROFILE_ENDPOINT,
  USER_REPORT_ENDPOINT,
  USER_CHANGE_PASSWORD_ENDPOINT,
  USER_DELETE_ACCOUNT_ENDPOINT,
  USER_MY_PROFILE_ENDPOINT,
  USER_MY_RESTRICTION_ENDPOINT,
  INTERCOM_ENDPOINT,
  STREAM_BAN_USER_ENDPOINT,
  ONEAPP_USER_RESOLVE_ENDPOINT,
  ONEAPP_INTERCOM_JWT_ENDPOINT,
  ONEAPP_ACCEPT_TERMS_ENDPOINT,
} from './config/api';
import { Group } from '@cobu/types';

export interface PublicProfileUser extends UserInfo {
  role: {
    name: string;
  };
}

export interface PublicProfileResponse {
  user: PublicProfileUser;
  groups: Array<{
    uuid: string;
    name: string;
    imageUrl?: string;
    meta: {
      groupMemberCount: number;
      upcomingGroupEventCount: number;
    };
  }>;
  sharedGroups: Array<{
    uuid: string;
    name: string;
    imageUrl?: string;
    meta: {
      groupMemberCount: number;
      upcomingGroupEventCount: number;
    };
  }>;
}

export interface OneAppUserResolveRequest {
  authToken: string;
  homebodyUserUuid: string;
  emailAddress: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  buildingName?: string;
  cid?: string;
  clientId?: string;
  buildingId?: string;
  propertyId?: string;
  leaseStart?: string;
  leaseEnd?: string;
  leaseStatus?: string;
  link?: string;
  deeplink?: string;
  appVersion?: string;
  language?: 'en' | 'es';
}

export interface BundledFeatureFlags {
  chatPolling?: boolean;
  tooltipPolling?: boolean;
  DM?: boolean;
}

export interface OneAppUserResolveResponse {
  message?: string;
  user: UserInfo;
  token: string;
  streamToken?: string;
  source?: string;
  partner?: {
    key: string;
    name: string;
  };
  link?: string;
  featureFlags?: BundledFeatureFlags | null;
  buildingConfig?: Record<string, any> | null;
  restriction?: Restriction | null;
  // Only present until the resident accepts the terms. Signed (not encrypted) and
  // carries the resident's email/phone — keep it in memory, never log it.
  pendingContactToken?: string;
}

export interface OneAppAcceptTermsRequest {
  pendingContactToken: string;
  agreedToCoC?: boolean;
  optedInSMSMessages?: boolean;
}

export interface IntercomJwtResponse {
  intercomJwt: string;
}

class UserService {
  async getUserProfile(userId: string): Promise<PublicProfileResponse> {
    const response = await authenticatedApiClient.get<PublicProfileResponse>(`/publicprofile/${userId}`);
    return response;
  }

  async updateUser(userId: string, userData: Partial<UserInfo>): Promise<UserInfo> {
    const response = await authenticatedApiClient.patch<UserInfo>(`/user/${userId}`, userData);
    return response;
  }

  async changePassword(newPassword: string): Promise<void> {
    await authenticatedApiClient.patch(USER_CHANGE_PASSWORD_ENDPOINT, {
      password: newPassword,
    });
  }

  async deleteAccount(): Promise<void> {
    await authenticatedApiClient.delete(USER_DELETE_ACCOUNT_ENDPOINT);
  }

  async uploadProfileImage(userId: string, imagePathOrFile: string | File): Promise<{ profileImage: string }> {
    const formData = new FormData();

    if (typeof imagePathOrFile === 'string') {
      const image = {
        uri: imagePathOrFile,
        type: 'image/jpeg',
        name: 'event_image.jpg',
      };
      formData.append('image', image as unknown as Blob);
    } else {
      formData.append('image', imagePathOrFile, imagePathOrFile.name);
    }

    const config = {
      headers: {
        'content-type': 'multipart/form-data',
      }
    };

    const response = await authenticatedApiClient.post<{ profileImage: string }>(
      `/user/${userId}/upload-profile-image`,
      formData,
      config
    );

    return response;
  }

  async createWelcomeMessage(message: string): Promise<any> {
    const config = {
      headers: {
        'content-type': 'multipart/form-data',
      }
    };
    const formData = new FormData();
    formData.append('contents', message);
    formData.append('type', 'WELCOME_MSG');

    const response = await authenticatedApiClient.post('/v2/groupcomment', formData, config);
    return response;
  }

  async getPublicProfile(userId: string): Promise<{
    user: UserInfo;
    groups: Group[];
    sharedGroups: Group[];
  }> {
    const response = await authenticatedApiClient.get<{
      user: UserInfo;
      groups: Group[];
      sharedGroups: Group[];
    }>(`${USER_PUBLIC_PROFILE_ENDPOINT}/${userId}`);
    return response;
  }

  async getUserById(userId: string): Promise<UserInfo> {
    const response = await this.getPublicProfile(userId);
    return response.user;
  }

  async getMyProfile(): Promise<UserInfo> {
    const response = await authenticatedApiClient.get<UserInfo>(USER_MY_PROFILE_ENDPOINT);
    return response;
  }

  async fetchMyRestriction(): Promise<MyRestrictionResponse> {
    const response = await authenticatedApiClient.get<MyRestrictionResponse>(
      USER_MY_RESTRICTION_ENDPOINT
    );
    return response;
  }

  async reportUser(params: {
    reportedUserUuid: string;
    reportedByUuid: string;
    reason: string;
    reportItem: {
      origin: string;
      groupEvent?: Gathering;
      user?: UserInfo;
    };
  }): Promise<any> {
    const response = await authenticatedApiClient.post(USER_REPORT_ENDPOINT, params);
    return response;
  }

  async getIntercomData(): Promise<any> {
    return authenticatedApiClient.get(INTERCOM_ENDPOINT);
  }

  async resolveOneAppUser(params: OneAppUserResolveRequest): Promise<OneAppUserResolveResponse> {
    const response = await unauthenticatedApiClient.post<OneAppUserResolveResponse>(
      ONEAPP_USER_RESOLVE_ENDPOINT,
      params
    );
    return response;
  }

  // Accepts the terms and, in the same write, saves the email/phone carried by the
  // pending-contact token. Either field may be skipped (already used by another
  // account) — read them off the returned user rather than assuming they were saved.
  async acceptTerms(params: OneAppAcceptTermsRequest): Promise<UserInfo> {
    const response = await authenticatedApiClient.post<UserInfo>(
      ONEAPP_ACCEPT_TERMS_ENDPOINT,
      params
    );
    return response;
  }

  async fetchIntercomJwt(): Promise<IntercomJwtResponse> {
    const response = await authenticatedApiClient.get<IntercomJwtResponse>(
      ONEAPP_INTERCOM_JWT_ENDPOINT,
    );
    return response;
  }

  async banUserOnDM(params: {
    channelId: string;
    blockingUserId: string;
    blockedUserId: string;
    unban?: boolean;
  }): Promise<{ success?: boolean; message?: string; data?: any }> {
    const response = await authenticatedApiClient.post<{ success?: boolean; message?: string; data?: any }>(
      STREAM_BAN_USER_ENDPOINT,
      params
    );
    if (response && 'data' in response && !('success' in response)) {
      return { success: true };
    }
    return response;
  }
}

const userServiceInstance = new UserService();
export default userServiceInstance;